import React, { useState, useEffect, useId, useRef } from "react";
import WistiaScripts from "./wistiaScripts";
import * as c from "./wistiaVideoEmbed.module.scss";
import classNames from "classnames";
import useWistiaPlayer from "@hooks/useWistiaPlayer";
import WistiaVideoEmbedCustomPlayButton from "./wistiaVideoEmbedCustomPlayButton";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";

export default function WistiaVideoEmbed({
  className,
  wistiaVideoId,
  wistiaOptions = "seo=false videoFoam=true",
  trackedEventName = null,
  useCustomPlayButton = false,
}) {
  const [isSSR, setIsSSR] = useState(true);
  const [isInView, setIsInView] = useState(false);

  const wistiaContainerId = useId();
  const containerRef = useRef(null);

  // Since wistia injects into the DOM when it has loaded clientside, we have
  // to make sure it isn't rendered on the server or else we'll get a mismatch
  // between the server and client during hydration
  useEffect(() => {
    setIsSSR(false);
  }, []);

  useGSAP(() => {
    if (!isSSR) {
      gsap.registerPlugin(ScrollTrigger);
      ScrollTrigger.create({
        trigger: containerRef.current,
        start: () => "top 100%+=1200px",
        // load video when 300px from to bottom of the viewport, to ensure no flash of unloaded content
        invalidateOnRefresh: true,
        once: true,
        onEnter: () => {
          setIsInView(true);
        },
      });
    }
  }, [isInView, isSSR]);

  const { wistiaPlayer } = useWistiaPlayer({
    wistiaContainerId,
    trackedEventName,
  });

  // Set wistiaOptions to have playButton=false when using custom play button
  const combinedWistiaOptions = wistiaOptions.includes("playButton=")
    ? wistiaOptions
    : `${wistiaOptions}${useCustomPlayButton ? " playButton=false" : ""}`;

  return (
    <div className={classNames(className, c.wistiaVideoEmbed)} ref={containerRef} data-transition="slide-enter">
      {!isSSR && isInView && (
        <>
          <WistiaScripts wistiaVideoId={wistiaVideoId} />
          <div className="wistia_responsive_padding">
            <div className="wistia_responsive_wrapper">
              <div
                id={wistiaContainerId} // Set an id so we have a unique identifier to hook into using the wistia api
                className={classNames("wistia_embed", `wistia_async_${wistiaVideoId}`, combinedWistiaOptions)}
              >
                <div className="wistia_swatch">
                  <img
                    src={`https://fast.wistia.com/embed/medias/${wistiaVideoId}/swatch`}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                  />
                </div>
                {useCustomPlayButton && <WistiaVideoEmbedCustomPlayButton wistiaPlayer={wistiaPlayer} />}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
